import putProfile from '@/requests/putProfile';
import putProfileCurrentSubject from '@/requests/putProfileCurrentSubject';
import useUserStore from '@/stores/useUserStore';
import { mutate } from 'swr';
import { getAuthToken } from './authToken';
import getDomainPlatform, { DomainPlatformValue } from './getDomainPlatform';
import { clearLegacyFsSubject, getSubject } from './subject';

interface Location {
  unit?: number;
  section?: number;
}

// 队列和确认位置只属于当前会话，换账号后不能继续发送旧账号的操作
let fsWrites:
  | {
      token: ReturnType<typeof getAuthToken>;
      userId: string;
      last: Promise<void>;
      pending: number;
      confirmed: Location;
    }
  | undefined;

/**
 * FS 平台写账号当前所在的位置。位置跨学科，写入必须按操作顺序落到服务端：
 * - 上一次写入结束（无论成败）后才发下一次，服务端最终停在最后一次操作的位置；
 * - 写入失败且之后没有新的写入时，本地回到服务端已确认的位置。
 * 写入涉及学科变化时重新验证档案（key 与 useProfile / FsCurrentSubjectSync 一致），
 * 外壳的当前学科随之同步；仅切单元不产生额外请求。
 */
async function writeFsLocation(
  location: Location,
  subject: number,
  request: (token: ReturnType<typeof getAuthToken>) => Promise<void>,
) {
  const token = getAuthToken();
  const { id: userId, unit, section } = useUserStore.getState();
  if (!fsWrites || fsWrites.token !== token || fsWrites.userId !== userId) {
    fsWrites = {
      token,
      userId,
      last: Promise.resolve(),
      pending: 0,
      confirmed: { unit, section },
    };
  }
  const queue = fsWrites;
  const isCurrentSession = () =>
    fsWrites === queue &&
    getAuthToken() === token &&
    useUserStore.getState().id === userId;
  if (queue.pending === 0) queue.confirmed = { unit, section };
  queue.pending += 1;
  useUserStore.setState(location);

  const send = () => (isCurrentSession() ? request(token) : undefined);
  const write = queue.last.then(send, send);
  queue.last = write;

  try {
    await write;
    if (!isCurrentSession()) return;
    queue.confirmed = location;
    // 服务端已记住当前所在学科，浏览器里改造前留下的旧学科不再参与落点判断
    clearLegacyFsSubject();
    if (useUserStore.getState().currentSubject?.id !== String(subject)) {
      mutate(['/users/profile', { platform: DomainPlatformValue.Fs }]).catch(
        () => {},
      );
    }
  } catch (error) {
    if (!isCurrentSession()) return;
    if (queue.last === write) useUserStore.setState(queue.confirmed);

    throw error;
  } finally {
    queue.pending -= 1;
  }
}

/**
 * 设置用户资料中的教材单元和课
 * @param param0 单元值使用未定义时同时清空单元和课，课值使用未定义时只清空课
 */
export default async function setProfileTextbook({
  unitId,
  sectionId,
  subjectId: givenSubjectId,
}: {
  unitId?: number;
  sectionId?: number;
  /**
   * 学科 id；不传时使用站点学科（getSubject()）。FS 平台没有站点学科，必须传
   */
  subjectId?: number;
}) {
  const subjectId = givenSubjectId ?? Number(getSubject().id);
  if (!subjectId) throw new Error('学科不存在');

  if (unitId === undefined && sectionId !== undefined) {
    throw new Error('当设置课时，必须同时设置单元');
  }

  const data = {
    subject: subjectId,
    unit: unitId ?? null,
    section: sectionId ?? null,
  };

  if (getDomainPlatform() === DomainPlatformValue.Fs) {
    await writeFsLocation(
      { unit: unitId, section: sectionId },
      subjectId,
      (token) => putProfile(data, token),
    );
    return;
  }

  const previousState = useUserStore.getState();

  useUserStore.setState({
    unit: unitId,
    section: sectionId,
  });

  try {
    await putProfile(data);
  } catch (error) {
    const currentState = useUserStore.getState();

    if (currentState.unit === unitId && currentState.section === sectionId) {
      useUserStore.setState({
        unit: previousState.unit,
        section: previousState.section,
      });
    }

    throw error;
  }
}

/**
 * FS 平台：回到某学科已有的教材档案所在的位置，并把这个学科标为账号当前所在的学科。
 * 只换标记，不改档案里的单元和课
 */
export async function resumeProfileTextbook({
  subjectId,
  unitId,
  sectionId,
}: {
  subjectId: number;
  unitId: number;
  sectionId?: number;
}) {
  await writeFsLocation(
    { unit: unitId, section: sectionId },
    subjectId,
    (token) => putProfileCurrentSubject({ subject: subjectId }, token),
  );
}
